import { Autocomplete, Divider, MenuItem, Typography } from '@mui/material'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchCustomersForGlobalServiceStation } from 'src/api/service-station'
import { AppTextField } from 'src/components/AppTextField'
import { MainButton } from 'src/components/MainButton'
import { CenteredSpinner } from 'src/components/Spinner'
import { DataTypes, IMainComponentType as Type } from 'src/enums'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { getTranslatedText } from 'src/utils'
import { findCustomPropertyByKey } from 'src/utils/customPropertyValues'
import { useGetFile } from '../../../api/file'
import { useGetMainComponentCustomProperties, useGetMainComponentTypeList } from '../../../api/main-component-type'
import { AppForm, AppFormProps, FormTextField } from '../../../components/FormCore'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { IFile } from '../../../generated-types'
import { ICustomProperty } from '../../../generated-types/customProperty'
import { IFormType } from '../../../generated-types/form-type'
import { IMainComponent } from '../../../generated-types/main-component'
import { useZodForm } from '../../../hooks/zod-form'
import { CustomMainComponentFields } from './CustomMainComponentFields'
import MainComponentStaticFormFields from './MainComponentStaticFormFields'
import { addCustomFields, getMainComponentSchema } from './mainComponentSchema'
interface MainComponentFormProps extends Omit<AppFormProps<IMainComponent>, 'onSubmit'> {
  onSubmitAlter?: (form: any, isCloneable: boolean, formContext?: any) => void
  onSubmit: (form: any, isCloneable?: boolean, formContext?: any) => void
  title: string
  customerId: EntityId
  selectedCustomerId?: EntityId
  setSelectedCustomerId?: React.Dispatch<React.SetStateAction<EntityId | undefined>>
  serviceStationId?: EntityId
  isCustomerSelectionEnable?: boolean
  isUpdateCustomerEnable?: boolean
  isSubmitting?: boolean
  resetFormData?: (form) => void
}
interface MainComponentStaticFormFieldsRef {
  handleSave: () => void
  validateProperties: () => void
}
export const MainComponentForm: React.FC<MainComponentFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  onSubmitAlter,
  title,
  customerId,
  setSelectedCustomerId,
  serviceStationId,
  isCustomerSelectionEnable = false,
  isUpdateCustomerEnable = true,
  selectedCustomerId,
  isSubmitting = false,
  resetFormData,
}) => {
  const [mainComponentTypeId, setmainComponentTypeId] = useState<EntityId>(initialValues?.typeId as EntityId)
  const [search, setSearch] = useState<string | undefined>(undefined)
  const { data: customFields } = useGetMainComponentCustomProperties(
    mainComponentTypeId,
    (selectedCustomerId ?? customerId) as EntityId,
  )
  const { data: mainComponentTypes, isLoading } = useGetMainComponentTypeList(
    (selectedCustomerId ?? customerId) as EntityId,
  )
  const { data } = useGetFile(initialValues?.fileId)
  const { data: certificateFileData } = useGetFile(initialValues?.certificate?.fileId)
  const { data: customersList, isLoading: isLoadingCustomers } = useSearchCustomersForGlobalServiceStation(
    customerId,
    serviceStationId,
    search,
  )
  const [customerError, setCustomerError] = useState<boolean>(false)
  const [mainComponentTypeError, setmainComponentTypeError] = useState<boolean>(false)
  const [initialCertificateFiles, setInitialCertificateFiles] = useState<IFile | null>()
  const [errorMargin, setErrorMargin] = useState({
    hs: '0px',
    vC5: '0px',
    vC15: '0px',
  })
  const [mainComponentTypeDetails, setMainComponentTypeDetails] = useState<{
    mainComponentType: IMainComponentType | undefined
    isCertificateDetailsRequired: boolean
  }>({
    mainComponentType: mainComponentTypes?.find(type => type.id === initialValues?.typeId) as IMainComponentType,
    isCertificateDetailsRequired: false,
  })
  const staticFormFieldsRef = useRef<MainComponentStaticFormFieldsRef>(null)
  const isUpdateForm = initialValues?.id
  const { t: translate } = useTranslation(['main-component', 'common', 'common-api-message', 'users'])
  useEffect(() => {
    const defaultValue = mainComponentTypes?.find(type => type.id === initialValues?.typeId)
    if (
      isCustomerSelectionEnable &&
      isUpdateCustomerEnable &&
      initialValues?.customerId !== selectedCustomerId &&
      isUpdateForm
    ) {
      setMainComponentTypeDetails({ mainComponentType: undefined, isCertificateDetailsRequired: false })
      return
    }
    if (!initialValues?.typeId) {
      setMainComponentTypeDetails({
        mainComponentType: mainComponentTypes?.[0] as IMainComponentType,
        isCertificateDetailsRequired: mainComponentTypes?.[0].componentType == Type.Net,
      })
      return
    }
    setMainComponentTypeDetails({
      ...{
        mainComponentType: defaultValue as IMainComponentType,
        isCertificateDetailsRequired: defaultValue?.componentType == Type.Net,
      },
    })
  }, [initialValues?.typeId, mainComponentTypes, isLoading])
  useEffect(() => {
    setmainComponentTypeId(mainComponentTypeDetails.mainComponentType?.id as EntityId)
  }, [mainComponentTypeDetails])

  const handleCustomProperties = useCallback(
    (f: string) => {
      if (!f) return {}
      const customFieldslist = JSON.parse(f)
      const customProperties = {}
      Object.keys(customFieldslist).forEach(fieldKey => {
        const value = customFieldslist[fieldKey]
        const fieldtype = findCustomPropertyByKey(fieldKey, customFields)
        if (value !== undefined && value !== null && value !== '' && fieldtype) {
          customProperties[fieldtype?.id.toString()] = value
        }
        if (fieldtype?.dataType === DataTypes.String && Array.isArray(value)) {
          customProperties[fieldtype.id.toString()] = (value as Array<string>)[0] ?? ''
        }
        if (fieldtype?.dataType === DataTypes.Integer && Array.isArray(value)) {
          customProperties[fieldtype.id.toString()] = (value as Array<string>)[0] ?? ''
        }
      })
      return customProperties
    },
    [customFields],
  )
  const form = useZodForm(
    (customFields as Array<ICustomProperty>)?.length > 0
      ? addCustomFields(
          isUpdateForm
            ? getMainComponentSchema(IFormType.Update, translate)
            : getMainComponentSchema(
                IFormType.Create,
                translate,
                mainComponentTypeDetails.isCertificateDetailsRequired,
              ),
          customFields,
          translate,
        )
      : isUpdateForm
      ? getMainComponentSchema(IFormType.Update, translate)
      : getMainComponentSchema(IFormType.Create, translate, mainComponentTypeDetails.isCertificateDetailsRequired),
    {
      defaultValues: {
        ...initialValues,
        ...handleCustomProperties(initialValues?.customPropertyValues || '{}'),
      },
    },
  )

  const handleSubmit = useCallback(
    async (f, isCloneable = false) => {
      const {
        certificateId,
        certificateNumber,
        certificateDate,
        validUntil,
        certificateDocumentType,
        certificateFileId,
        certificateSupplierId,
        validPeriod,
        ...rest
      } = f
      if (mainComponentTypeDetails.isCertificateDetailsRequired) {
        rest.certificate = {
          id: certificateId,
          certificateNumber,
          certificateDate,
          validUntil,
          certificateDocumentType,
          validPeriod,
          fileId: certificateFileId,
          supplierId: certificateSupplierId,
          mainComponentId: f.id,
        }
      }
      const customProperties = new Map<string, any>()
      customFields?.forEach(field => {
        const fieldKey = field.id.toString()
        const value = f[fieldKey]
        if (value !== undefined && value !== null && value !== '') {
          customProperties.set(fieldKey, value)
        }
        delete rest[fieldKey]
      })
      if (isCloneable) {
        try {
          await onSubmitAlter?.(
            { ...rest, customPropertyValues: JSON.stringify(Object.fromEntries(customProperties)) },
            isCloneable,
            form,
          )
          form.setValue('serialNumber', '')
          form.setValue('name', '')
          form.setValue('certificateNumber', '')
          form.setValue('certificateSupplierId', null)
          form.setValue('certificateDate', null)
          form.setValue('certificateFileId', null)
          setInitialCertificateFiles(null)
        } catch (error) {
          form.setValue('certificateNumber', '')
        }
      } else {
        try {
          await onSubmit(
            { ...rest, customPropertyValues: JSON.stringify(Object.fromEntries(customProperties)) },
            false,
            form,
          )
        } catch (error) {
          form.setValue('certificateNumber', '')
        }
      }
    },
    [onSubmitAlter, onSubmit, form, customFields, mainComponentTypeDetails.isCertificateDetailsRequired],
  )
  const setErrorMarginValues = useCallback(
    event => {
      const hs = form.getValues('hs')
      const vC5 = form.getValues('vC5')
      setErrorMargin({ hs: '0px', vC5: '0px', vC15: '0px' })
      if (hs != null && hs != '' && hs != undefined && (vC5 == null || vC5 == '')) {
        setErrorMargin(prev => ({ ...prev, hs: '20px', vC15: '20px' }))
      }
      if (vC5 != null && vC5 != '' && vC5 != undefined && (hs == null || hs == '')) {
        setErrorMargin(prev => ({ ...prev, vC5: '20px', vC15: '20px' }))
      }
      if (event != 'submit' && (hs === '' || hs == null) && (vC5 === '' || vC5 == null)) {
        setErrorMargin(prev => ({ ...prev, vC15: '20px' }))
      }
    },
    [form],
  )
  useEffect(() => {
    setInitialCertificateFiles(certificateFileData)
  }, [certificateFileData])
  useEffect(() => {
    if (mainComponentTypeDetails.mainComponentType) {
      form.setValue('typeId', mainComponentTypeDetails.mainComponentType.id)
    }
  }, [mainComponentTypeDetails])
  if (isLoading) {
    return <CenteredSpinner containerProps={{ height: 400, width: 600 }} />
  }
  const handleSubmitAlter = async (isClone: boolean) => {
    const hs = form.getValues('hs')
    const vC5 = form.getValues('vC5')
    const vC15 = form.getValues('vC15')
    if (hs && vC5 && vC15) {
      await new Promise(resolve => {
        staticFormFieldsRef.current?.handleSave()
        setTimeout(resolve, 0)
      })
      isClone
        ? form.handleSubmit(f => {
            handleSubmit(f, true)
          })()
        : form.handleSubmit(handleSubmit)()
    } else if (!hs && !vC5 && !vC15) {
      isClone
        ? form.handleSubmit(f => {
            handleSubmit(f, true)
          })()
        : form.handleSubmit(handleSubmit)()
    } else {
      staticFormFieldsRef.current?.validateProperties()
    }
  }
  return (
    <GenericDialogLayout
      title={title}
      pop={onCancel}
      actions={[
        <Fragment key={'main-component-form-actions'}>
          {!isUpdateForm && (
            <MainButton
              variant="contained"
              onClick={() => {
                setErrorMarginValues('submit')
                handleSubmitAlter(true)
              }}
            >
              {translate('add-clone')}
            </MainButton>
          )}
          <MainButton
            variant="contained"
            loading={isSubmitting}
            onClick={() => {
              setErrorMarginValues('submit')
              if (isCustomerSelectionEnable && !selectedCustomerId) {
                setCustomerError(true)
                return
              }
              if (!mainComponentTypeDetails.mainComponentType) {
                setmainComponentTypeError(true)
                return
              }
              handleSubmitAlter(false)
            }}
          >
            {isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
          </MainButton>
        </Fragment>,
      ]}
      removedCloseButton={false}
    >
      <AppForm form={form} onSubmit={handleSubmit}>
        {isCustomerSelectionEnable && (
          <Fragment>
            <Autocomplete
              options={customersList || []}
              onChange={(_, value) => {
                setSelectedCustomerId && setSelectedCustomerId(value?.id)
                setCustomerError(false)
                resetFormData && resetFormData(form)
              }}
              value={customersList?.find(c => c.id === selectedCustomerId) ?? null}
              defaultValue={customersList?.find(c => c.id === selectedCustomerId)}
              getOptionLabel={o => o.name}
              loading={isLoadingCustomers}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={translate('customers', { ns: 'users' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  onChange={e => {
                    setSearch(e.target.value)
                  }}
                  placeholder={translate('customer-select', { ns: 'main-component' })}
                />
              )}
              inputValue={customersList?.find(c => c.id === selectedCustomerId)?.name ?? search}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={!isUpdateCustomerEnable}
              clearIcon={null}
            />
            {customerError && (
              <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
                {translate('select-valid-customer', { ns: 'main-component' })}
              </Typography>
            )}
          </Fragment>
        )}
        <FormTextField type="hidden" name="customPropertyValues" value={'test'} sx={{ display: 'none' }} />
        {((!initialValues?.typeId && mainComponentTypeDetails.mainComponentType) ||
          (isCustomerSelectionEnable && isUpdateCustomerEnable && isUpdateForm)) && (
          <Fragment>
            <Autocomplete
              onChange={(_, value) => {
                const mainComponentType = value as IMainComponentType
                setMainComponentTypeDetails({
                  mainComponentType: mainComponentType,
                  isCertificateDetailsRequired: false,
                })
                form.setValue('typeId', mainComponentType?.id)
                setmainComponentTypeError(false)
              }}
              options={mainComponentTypes || []}
              value={mainComponentTypes?.find(x => x.id === mainComponentTypeDetails.mainComponentType?.id) || null}
              defaultValue={mainComponentTypes?.find(x => x.id === mainComponentTypeDetails.mainComponentType?.id)}
              getOptionLabel={o => getTranslatedText(o.name)}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {getTranslatedText(o.name)}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={translate('main-component-type', { ns: 'main-component' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
            {mainComponentTypeError && (
              <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
                {translate('select-valid-main-component-type', { ns: 'main-component' })}
              </Typography>
            )}
          </Fragment>
        )}
        {(mainComponentTypeDetails.mainComponentType ||
          (isCustomerSelectionEnable && isUpdateCustomerEnable && isUpdateForm)) && (
          <Fragment>
            <MainComponentStaticFormFields
              ref={staticFormFieldsRef}
              form={form}
              initialValues={initialValues}
              file={data}
              initialCertificateFiles={initialCertificateFiles}
              setInitialCertificateFiles={setInitialCertificateFiles}
              customerId={(selectedCustomerId ?? customerId) as EntityId}
              isUpdateForm={isUpdateForm}
              mainComponentTypeDetails={mainComponentTypeDetails}
              setMainComponentTypeDetails={setMainComponentTypeDetails}
              setErrorMarginValues={setErrorMarginValues}
              setErrorMargin={setErrorMargin}
              errorMargin={errorMargin}
            />
            {customFields && customFields?.length > 0 && <Divider className="pt-4" />}
            <CustomMainComponentFields customFields={customFields as Array<ICustomProperty>} />
          </Fragment>
        )}
      </AppForm>
    </GenericDialogLayout>
  )
}
export default MainComponentForm
